import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { RiLockLine, RiEyeLine, RiEyeOffLine } from 'react-icons/ri';
import '../styles/ResetPassword.css';
import { useApiCall } from '../hooks/useApiCall';

const ResetPassword = () => {
  const apiCall = useApiCall();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [isVerifying, setIsVerifying] = useState(true);

  useEffect(() => {
    // Verify token on mount
    if (!token) {
      setTokenValid(false);
      setIsVerifying(false);
      return;
    }

    apiCall('auth/verify-reset-token', 'POST', { token }, null, null).then((response) => {
      setTokenValid(response.success);
      setIsVerifying(false);
      if (!response.success) {
        setErrors({ general: response?.response || 'Invalid or expired reset link.' });
      }
    });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);

    apiCall('auth/reset-password', 'POST', {
      token: token,
      password: formData.password.trim()
    }, null, null).then((response) => {
      if(response.success){
        // Redirect to signin with success message
        navigate('/sign-in?reset=success');
      } else {
        setErrors({ general: response?.response || 'An error occurred. Please try again.' });
      }
      setIsLoading(false);
    });
  };

  if (isVerifying) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-content">
          <div className="reset-password-form-wrapper">
            <div className="verifying-message">
              <div className="spinner"></div>
              <p>Verifying reset link...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (tokenValid === false) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-content">
          <div className="reset-password-form-wrapper">
            <div className="reset-password-header">
              <h1>Invalid or Expired Link</h1>
              <p className="error-text">
                This password reset link is invalid or has expired. 
                Password reset links are valid for 1 hour.
              </p>
            </div>

            <div className="reset-password-footer">
              <Link to="/forgot-password" className="request-new-link">
                Request New Reset Link
              </Link>
              <Link to="/sign-in" className="back-to-signin">
                Back to Sign In
              </Link>
            </div>
          </div>

          <div className="reset-password-image">
            <div className="image-content">
              <h2>Building Depot Suriname</h2>
              <p>Lights & Living</p>
              <div className="image-placeholder">
                <img 
                  src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                  alt="Modern lighting display"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      <div className="reset-password-content">
        <div className="reset-password-form-wrapper">
          <div className="reset-password-header">
            <h1>Reset Your Password</h1>
            <p>Enter your new password below</p>
          </div>

          <form onSubmit={handleSubmit} className="reset-password-form">
            {errors.general && (
              <div className="error-message general-error">
                {errors.general}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="password">New Password</label>
              <div className="input-wrapper">
                <RiLockLine className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter new password"
                  className={errors.password ? 'error' : ''}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <RiEyeOffLine /> : <RiEyeLine />}
                </button>
              </div>
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <div className="input-wrapper">
                <RiLockLine className="input-icon" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirm new password"
                  className={errors.confirmPassword ? 'error' : ''}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <RiEyeOffLine /> : <RiEyeLine />}
                </button>
              </div>
              {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
            </div>

            <button 
              type="submit" 
              className="reset-btn"
              disabled={isLoading}
            >
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>

          <div className="reset-password-footer">
            <Link to="/sign-in" className="back-to-signin">
              Back to Sign In
            </Link>
          </div>
        </div>

        <div className="reset-password-image">
          <div className="image-content">
            <h2>Building Depot Suriname</h2>
            <p>Lights & Living</p>
            <div className="image-placeholder">
              <img 
                src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                alt="Modern lighting display"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
